import { generateClient } from 'aws-amplify/api';

export interface MantleSummaryDebugModel {
  id: string;
  inputPriceUsdPer1MTokens: number | null;
  outputPriceUsdPer1MTokens: number | null;
  priceAvailable: boolean;
}

export interface MantleSummaryDebugQuote extends MantleSummaryDebugModel {
  documentVersionId: string;
  previousDocumentVersionId: string | null;
  previousDocumentVersionNumber: number | null;
  modelId: string;
  quoteToken: string;
  estimatedInputTokens: number;
  maxOutputTokens: number;
  estimatedMaxCostUsd: number | null;
}

export interface MantleSummaryDebugResult extends MantleSummaryDebugModel {
  documentVersionId: string;
  previousDocumentVersionId: string | null;
  previousDocumentVersionNumber: number | null;
  modelId: string;
  summary: string;
  inputTokens: number;
  outputTokens: number;
  actualCostUsd: number | null;
}

const getModelsQuery = `
  query GetMantleSummaryDebugModels {
    getMantleSummaryDebugModels {
      id
      inputPriceUsdPer1MTokens
      outputPriceUsdPer1MTokens
      priceAvailable
    }
  }
`;

const quoteDocumentMutation = `
  mutation QuoteMantleDocumentSummaryDebug(
    $documentVersionId: ID!
    $modelId: String!
    $systemPrompt: String
  ) {
    quoteMantleDocumentSummaryDebug(
      documentVersionId: $documentVersionId
      modelId: $modelId
      systemPrompt: $systemPrompt
    ) {
      documentVersionId
      previousDocumentVersionId
      previousDocumentVersionNumber
      modelId
      quoteToken
      inputPriceUsdPer1MTokens
      outputPriceUsdPer1MTokens
      priceAvailable
      estimatedInputTokens
      maxOutputTokens
      estimatedMaxCostUsd
    }
  }
`;

const generateDocumentMutation = `
  mutation GenerateMantleDocumentSummaryDebug(
    $documentVersionId: ID!
    $modelId: String!
    $quoteToken: String!
    $systemPrompt: String
  ) {
    generateMantleDocumentSummaryDebug(
      documentVersionId: $documentVersionId
      modelId: $modelId
      quoteToken: $quoteToken
      systemPrompt: $systemPrompt
    ) {
      documentVersionId
      previousDocumentVersionId
      previousDocumentVersionNumber
      modelId
      summary
      inputTokens
      outputTokens
      inputPriceUsdPer1MTokens
      outputPriceUsdPer1MTokens
      priceAvailable
      actualCostUsd
    }
  }
`;

const saveDocumentMutation = `
  mutation SaveMantleDocumentSummaryDebug($documentVersionId: ID!, $summary: String!) {
    saveMantleDocumentSummaryDebug(documentVersionId: $documentVersionId, summary: $summary) {
      saved
    }
  }
`;

type GraphQLResponse<T> = {
  data: T;
  errors?: Array<{ message: string }>;
};

const MANTLE_MODEL_OVERLOADED_MESSAGE = [
  'The selected Mantle model is temporarily overloaded.',
  'Please try again in a few moments or choose another model.',
].join(' ');
const GENERIC_REQUEST_ERROR_MESSAGE = 'The request could not be completed.';

const getErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof Error) return error.message;
  if (!error || typeof error !== 'object') return undefined;

  const errorWithDetails = error as {
    message?: unknown;
    errors?: Array<{ message?: unknown }>;
  };
  if (typeof errorWithDetails.message === 'string') return errorWithDetails.message;

  return errorWithDetails.errors
    ?.map(({ message }) => message)
    .find((message): message is string => typeof message === 'string');
};

const getClientErrorMessage = (error: unknown): string => {
  const message = getErrorMessage(error);
  if (/status 529|overloaded/i.test(message || '')) {
    return MANTLE_MODEL_OVERLOADED_MESSAGE;
  }

  return message || GENERIC_REQUEST_ERROR_MESSAGE;
};

const request = async <T>(query: string, variables?: Record<string, string>): Promise<T> => {
  const client = generateClient();
  let response: GraphQLResponse<T>;

  try {
    response = await client.graphql({ query, variables }) as unknown as GraphQLResponse<T>;
  } catch (error: unknown) {
    throw new Error(getClientErrorMessage(error));
  }

  if (response.errors?.length) {
    throw new Error(getClientErrorMessage({ errors: response.errors }));
  }

  return response.data;
};

export const getMantleSummaryDebugModels = async (): Promise<MantleSummaryDebugModel[]> => {
  const data = await request<{ getMantleSummaryDebugModels: MantleSummaryDebugModel[] }>(getModelsQuery);
  return data.getMantleSummaryDebugModels;
};

export const quoteMantleDocumentSummaryDebug = async (
  documentVersionId: string,
  modelId: string,
  systemPrompt?: string,
): Promise<MantleSummaryDebugQuote> => {
  const data = await request<{ quoteMantleDocumentSummaryDebug: MantleSummaryDebugQuote }>(
    quoteDocumentMutation,
    {
      documentVersionId,
      modelId,
      ...(systemPrompt === undefined ? {} : { systemPrompt }),
    },
  );
  return data.quoteMantleDocumentSummaryDebug;
};

export const generateMantleDocumentSummaryDebug = async (
  documentVersionId: string,
  modelId: string,
  quoteToken: string,
  systemPrompt?: string,
): Promise<MantleSummaryDebugResult> => {
  const data = await request<{ generateMantleDocumentSummaryDebug: MantleSummaryDebugResult }>(
    generateDocumentMutation,
    {
      documentVersionId,
      modelId,
      quoteToken,
      ...(systemPrompt === undefined ? {} : { systemPrompt }),
    },
  );
  return data.generateMantleDocumentSummaryDebug;
};

export const saveMantleDocumentSummaryDebug = async (
  documentVersionId: string,
  summary: string,
): Promise<void> => {
  const data = await request<{ saveMantleDocumentSummaryDebug: { saved: boolean } }>(
    saveDocumentMutation,
    { documentVersionId, summary },
  );
  if (!data.saveMantleDocumentSummaryDebug.saved) {
    throw new Error('The document summary was not saved.');
  }
};
